import { theme } from 'antd';

export const sageCreamPalette = {
    pageBg: '#F7F5EF',
    pageBgSoft: '#F1F0E9',
    cardBg: '#FFFEFA',
    cardBgMuted: '#F8F7F1',
    sage: '#4F7B58',
    sageDark: '#31583A',
    sageSoft: '#E6EDE4',
    sagePale: '#F0F4ED',
    text: '#1F211C',
    textMuted: '#62685D',
    border: '#E0DED4',
    borderStrong: '#D4D0C4',
    amber: '#D99A31',
    orange: '#D7742F',
} as const;

// LIGHT
export const freshGreenTheme = {
    token: {
        colorPrimary: sageCreamPalette.sage,
        colorPrimaryHover: '#5F8C68',
        colorPrimaryActive: sageCreamPalette.sageDark,
        colorInfo: sageCreamPalette.sage,
        colorSuccess: sageCreamPalette.sage,
        colorWarning: sageCreamPalette.amber,
        colorError: '#D92D20',
        colorErrorHover: '#B42318',
        colorErrorActive: '#912018',
        colorErrorBg: '#FEF3F2',
        colorErrorBorder: '#FDA29B',
        colorErrorText: '#7A271A',
        colorTextBase: sageCreamPalette.text,
        colorTextSecondary: sageCreamPalette.textMuted,
        colorBorder: sageCreamPalette.border,
        colorBorderSecondary: '#ECE9DF',
        colorBgBase: sageCreamPalette.pageBg,
        colorBgContainer: sageCreamPalette.cardBg,
        colorBgElevated: sageCreamPalette.cardBg,
        borderRadius: 14,
        borderRadiusLG: 18,
        borderRadiusSM: 10,
        controlHeight: 40,
        fontFamily: '"Inter", Arial, Helvetica, sans-serif',
        boxShadow: '0 12px 28px rgba(49, 55, 43, 0.08)',
        boxShadowSecondary: '0 6px 18px rgba(49, 55, 43, 0.06)',
    },
    components: {
        Typography: {
            fontSize: 15,
        },
        Card: {
            borderRadius: 18,
            colorBgContainer: sageCreamPalette.cardBg,
            colorBorderSecondary: sageCreamPalette.border,
            boxShadow: '0 10px 28px rgba(49, 55, 43, 0.07)',
        },
        Layout: {
            headerBg: 'rgba(247, 245, 239, 0.92)',
            bodyBg: sageCreamPalette.pageBg,
            footerBg: sageCreamPalette.pageBg,
            siderBg: sageCreamPalette.pageBg,
            triggerBg: sageCreamPalette.sage,
            triggerColor: '#FFFFFF',
        },
        Menu: {
            itemBg: sageCreamPalette.pageBg,
            itemColor: sageCreamPalette.textMuted,
            itemHoverBg: sageCreamPalette.sagePale,
            itemSelectedBg: sageCreamPalette.sage,
            itemSelectedColor: '#FFFFFF',
            itemActiveBg: sageCreamPalette.sagePale,
        },
        Tag: {
            defaultBg: sageCreamPalette.sagePale,
            defaultColor: sageCreamPalette.sageDark,
        },
        Badge: { colorPrimary: sageCreamPalette.amber },
        Button: {
            borderRadius: 14,
            defaultBorderColor: sageCreamPalette.border,
            defaultBg: sageCreamPalette.cardBg,
            defaultHoverBorderColor: sageCreamPalette.sage,
            defaultShadow: 'none',
            primaryShadow: 'none',
            dangerShadow: 'none',
            controlHeightLG: 48,
        },
        Input: {
            borderRadius: 16,
            colorBgContainer: sageCreamPalette.cardBg,
        },
        Select: {
            borderRadius: 14,
            colorBgContainer: sageCreamPalette.cardBg,
        },
        Tabs: {
            itemSelectedColor: sageCreamPalette.sageDark,
            inkBarColor: sageCreamPalette.sage,
        },
        Segmented: { itemSelectedBg: sageCreamPalette.sagePale },
    },
    algorithm: theme.defaultAlgorithm,
} as const;

// DARK
export const freshGreenDarkTheme = {
    ...freshGreenTheme,
    token: {
        colorPrimary: '#30D158',
        colorInfo: '#30D158',
        colorSuccess: '#30D158',
        colorWarning: '#FFB340',
        colorError: '#D61F1F',
        colorErrorHover: '#B91C1C',
        colorErrorActive: '#991B1B',
        colorErrorBg: '#2A1214',
        colorErrorBorder: '#7F1D1D',
        colorErrorText: '#FECACA',
        colorTextBase: '#EAEAEA',
        colorBorder: '#2C2C2C',
        colorBgBase: '#121212',
        controlHeight: 36,
    },
    components: {
        Typography: {
            fontSize: 15,
        },
        Layout: {
            headerBg: '#121212',
            bodyBg: '#121212',
            footerBg: '#121212',
            siderBg: '#121212',
            triggerBg: '#30D158',
            triggerColor: '#000000',
        },
        Menu: {
            itemBg: '#121212',
            itemColor: '#EAEAEA',
            itemHoverBg: '#1A1A1A',
            itemSelectedBg: '#30D158',
            itemSelectedColor: '#000000',
            itemActiveBg: '#1A1A1A',
        },
        Tag: {
            colorSuccess: '#30D158', // Dinner
            colorWarning: '#FFB340', // Calories
            colorError: '#4FA3FF',  // Meat
        },
        Badge: { colorPrimary: '#FFB340' },
        Button: {
            defaultBorderColor: '#2C2C2C',
            defaultBg: '#1A1A1A',
            defaultHoverBorderColor: '#30D158',
            defaultShadow: 'none',
            primaryShadow: 'none',
            dangerShadow: 'none',
        },
        Segmented: { itemSelectedBg: '#14281E' }, // subtle greenish dark
    },
    algorithm: theme.darkAlgorithm,
} as const;
